import React, { useState, useEffect } from 'react';

import ListPageHeader from '@shared/ListPageHeader';
import { Card, Table, Space, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, Link } from 'react-router-dom';

import { inventoryService } from '@services/admin/inventoryService';
import { inventoryHoldService } from '@services/admin/inventoryHoldService';
import { productService } from '@services/admin/productService';
import ProductInventoryTable from '@components/shared/products/ProductInventoryTable';

export default function ProductInventory() {
  const product = useLoaderData();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [inTransitItems, setInTransitItems] = useState([]);
  const [loadingInTransit, setLoadingInTransit] = useState(false);

  useEffect(() => {
    const fetchInventories = async () => {
      setLoading(true);
      try {
        const result = await inventoryService.getRecords({
          'products.id': [product.id],
          page: 1,
          page_size: 100,
          sorting_field: 'inventories.id',
          sorting_direction: 'desc'
        });

        if (result.success) {
          setInventories(result.data || []);
        } else {
          throw new Error(result.message || 'Failed to load inventories');
        }
      } catch (error) {
        console.error('Error fetching inventories:', error);
        message.error(t('common.error.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    const fetchInTransit = async () => {
      setLoadingInTransit(true);
      try {
        const result = await productService.fetchInTransit(product.id);
        if (result.success) {
          setInTransitItems(result.data || []);
        }
      } catch (error) {
        console.error('Error fetching in-transit items:', error);
      } finally {
        setLoadingInTransit(false);
      }
    };

    if (product?.id) {
      fetchInventories();
      fetchInTransit();
    }
  }, [product?.id, t]);

  const inTransitColumns = [
    {
      title: t('models.inbound.name'),
      dataIndex: 'inbound_name',
      key: 'inbound_name',
      render: (text, record) => (
        <Link to={`/inbounds/${record.inbound_id}`} style={{ color: '#1890ff' }}>
          {text}
        </Link>
      ),
    },
    {
      title: t('models.inbound.status'),
      dataIndex: 'inbound_status',
      key: 'inbound_status',
      render: (status) => (
        <Tag color={status === 'processing' ? 'orange' : 'blue'}>
          {t(`enums.inbound.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('models.inbound.eta'),
      dataIndex: 'eta',
      key: 'eta',
      render: (text) => text || '-',
    },
    {
      title: t('models.inventory.inventory_type'),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('models.inbound_item.scheduled_quantity'),
      dataIndex: 'scheduled_quantity',
      key: 'scheduled_quantity',
      align: 'right',
    },
    {
      title: t('models.inbound_item.putaway_quantity'),
      dataIndex: 'putaway_quantity',
      key: 'putaway_quantity',
      align: 'right',
    },
  ];

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['products', 'products.list', { label: `${product.sku} - ${product.name}` }, { label: t('shared.inventory.title') }]}
      />
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* In Stock Inventory */}
          <div>
            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px' }}>
              {t('shared.inventory.in_stock')} ({inventories.length})
            </div>
            <ProductInventoryTable
              inventories={inventories}
              loading={loading}
              inventoryService={inventoryService}
              holdService={inventoryHoldService}
              role="admin"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => t('common.pagination.total', { total }),
              }}
            />
          </div>

          {/* In Transit Inventory */}
          <div>
            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px' }}>
              {t('shared.inventory.in_transit')} ({inTransitItems.length})
            </div>
            <Table
              columns={inTransitColumns}
              dataSource={inTransitItems}
              rowKey="id"
              loading={loadingInTransit}
              pagination={false}
              locale={{ emptyText: t('common.table.no_data') }}
            />
          </div>
        </Space>
      </Card>
    </div>
  );
}
